import { useEffect, useRef, useState, type ElementType, type ReactNode } from 'react';

/**
 * Reveal — a tasteful, one-shot scroll/load entrance for marketing sections.
 *
 * Brand-aligned (BRANDING_GUIDELINES §11): a single subtle opacity + small
 * translateY lift on first viewport entry — NOT a fade-in-on-every-scroll, NOT
 * applied to body text. Used on section headers, cards, and hero pieces only.
 * Fully honors `prefers-reduced-motion`: when reduced, content is shown
 * immediately with no transform and no transition.
 *
 * `delay` staggers siblings (30–50ms steps per the motion guidance). Once an
 * element has revealed it stays revealed — no exit animation, no re-trigger.
 */

let prefersReduced = false;
if (typeof window !== 'undefined' && window.matchMedia) {
  prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

export function Reveal({
  as,
  children,
  className = '',
  delay = 0,
  y = 14,
}: {
  as?: ElementType;
  children: ReactNode;
  className?: string;
  /** Stagger in ms (e.g. index * 50). */
  delay?: number;
  /** Travel distance in px for the lift. */
  y?: number;
}) {
  const Tag: ElementType = as ?? 'div';
  const ref = useRef<HTMLElement | null>(null);
  const [shown, setShown] = useState(prefersReduced);

  useEffect(() => {
    if (prefersReduced) return;
    const node = ref.current;
    if (!node) return;
    if (typeof IntersectionObserver === 'undefined') {
      setShown(true);
      return;
    }

    let done = false;
    const reveal = () => {
      if (done) return;
      done = true;
      setShown(true);
      io.disconnect();
      clearTimeout(timer);
    };

    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          // Reveal when the element is in (or has already scrolled past) the
          // viewport — `boundingClientRect.top` guards the fast-scroll case
          // where an element is skipped before an intersecting frame is sampled.
          if (entry.isIntersecting || entry.boundingClientRect.top < window.innerHeight) {
            reveal();
            break;
          }
        }
      },
      { threshold: 0.1, rootMargin: '0px 0px -6% 0px' },
    );
    io.observe(node);

    // Safety net: never leave content invisible. If the observer hasn't fired
    // (slow frame, edge case), reveal anyway shortly after mount.
    const timer = window.setTimeout(reveal, 1400);

    return () => {
      io.disconnect();
      clearTimeout(timer);
    };
  }, []);

  return (
    <Tag
      ref={ref as never}
      className={className}
      style={
        prefersReduced
          ? undefined
          : {
              opacity: shown ? 1 : 0,
              transform: shown ? 'none' : `translateY(${y}px)`,
              transition: `opacity 600ms cubic-bezier(0.16,1,0.3,1) ${delay}ms, transform 600ms cubic-bezier(0.16,1,0.3,1) ${delay}ms`,
              willChange: shown ? undefined : 'opacity, transform',
            }
      }
    >
      {children}
    </Tag>
  );
}
